// @flow
/** @jsx jsx */

import { css, jsx } from '@emotion/core';

import { formatPriceToBeInCurrency } from './utils';

import { type Currency } from './types';

type Props = {
  /** Price in USD pennies (i.e. hundreths of a USD dollar) */
  children: number,
  /** Currency to show price in */
  currency?: Currency,
  /** Hide currency symbol */
  hideSymbol?: boolean,
  /** Use rounding relative to first digit */
  isFriendly?: boolean,
  /** Max number of digits to show */
  maxDecimals?: number,
  /** Minimum number of decimals to show */
  minDecimals?: number,
  /** Applies font size to currency symbol only */
  symbolFontSize?: string,
};

const defaultCurrency = {
  multiplier: 1,
  name: 'USD',
  symbol: '$',
};

const priceCss = css({
  alignItems: 'center',
  display: 'inline-flex',
});

/**
 * Displays a price in a user-friendly format
 */
const Price = ({
  children,
  currency = defaultCurrency,
  hideSymbol = false,
  isFriendly = false,
  maxDecimals = 2,
  minDecimals = 0,
  symbolFontSize = 'inherit',
}: Props) => {
  const absPrice = Math.abs(children);
  const isNegative = children < 0;
  const price = formatPriceToBeInCurrency(absPrice, currency, false, {
    maxDecimals,
    minDecimals,
    useRelativeRounding: isFriendly,
  });
  return (
    <span css={priceCss}>
      {isNegative && <span>-</span>}
      {!hideSymbol && (
        <span style={{ fontSize: symbolFontSize }}>{currency.symbol}</span>
      )}
      <span>{price}</span>
    </span>
  );
};

export default Price;
